import React, { PropsWithChildren, Fragment } from 'react';
import { Menu as HeadlessMenu } from '@headlessui/react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { Float } from '@headlessui-float/react';

type BaseMenuItemProps = PropsWithChildren<{
  icon?: React.ReactNode;
}>;

type MenuItemProps = BaseMenuItemProps & {
  activeClassName?: string;
  inactiveClassName?: string;
  className?: string;
  disabled?: boolean;
};

type ExternalLinkMenuItemProps = MenuItemProps & {
  href: string;
  onClick?: () => void;
};

type ReactRouterLinkMenuItemProps = MenuItemProps & {
  to: string;
};

type ButtonMenuItemProps = MenuItemProps & {
  onClick: React.MouseEventHandler<HTMLButtonElement> | null;
};

function styles(
  active: boolean,
  disabled?: boolean,
  inactiveClassName?: string,
  activeClassName?: string
) {
  return classNames(
    active
      ? activeClassName ?? 'bg-purple-100 text-primary'
      : inactiveClassName ?? 'text-primary',
    disabled ? 'text-primary/40 cursor-not-allowed' : '',
    'group flex items-center px-4 py-2 text-sm gap-3 font-semibold h-10'
  );
}

const ExternalLinkMenuItem: React.FunctionComponent<
  ExternalLinkMenuItemProps
> = ({
  href,
  activeClassName,
  inactiveClassName,
  className,
  disabled,
  onClick,
  ...rest
}) => {
  if (disabled) {
    return (
      <ButtonMenuItem
        onClick={onClick ?? null}
        activeClassName={activeClassName}
        inactiveClassName={inactiveClassName}
        className={className}
        disabled={disabled}
        {...rest}
      />
    );
  }
  return (
    <HeadlessMenu.Item>
      {({ active }) => (
        <a
          href={href}
          target="_blank"
          className={classNames(
            styles(active, disabled, inactiveClassName, activeClassName),
            className
          )}
          rel="noreferrer"
        >
          <BaseMenuItemContent {...rest} />
        </a>
      )}
    </HeadlessMenu.Item>
  );
};

const ReactRouterLinkMenuItem: React.FunctionComponent<
  ReactRouterLinkMenuItemProps
> = ({
  to,
  activeClassName,
  inactiveClassName,
  className,
  disabled,
  ...rest
}) => {
  return (
    <HeadlessMenu.Item>
      {({ active }) =>
        disabled ? (
          <span
            role="link"
            aria-disabled={disabled ? 'true' : undefined}
            className={classNames(
              styles(active, disabled, inactiveClassName, activeClassName),
              className
            )}
          >
            <BaseMenuItemContent {...rest} />
          </span>
        ) : (
          <Link
            role="link"
            aria-disabled={disabled ? 'true' : undefined}
            to={to}
            className={classNames(
              styles(active, disabled, inactiveClassName, activeClassName),
              className
            )}
          >
            <BaseMenuItemContent {...rest} />
          </Link>
        )
      }
    </HeadlessMenu.Item>
  );
};

const ButtonMenuItem: React.FunctionComponent<ButtonMenuItemProps> = ({
  onClick,
  activeClassName,
  inactiveClassName,
  className,
  disabled,
  ...rest
}) => {
  return (
    <HeadlessMenu.Item>
      {({ active }) => (
        <button
          disabled={disabled}
          type="button"
          onClick={onClick ?? undefined}
          className={classNames(
            styles(active, disabled, inactiveClassName, activeClassName),
            'w-full',
            className
          )}
        >
          <BaseMenuItemContent {...rest} />
        </button>
      )}
    </HeadlessMenu.Item>
  );
};

const BaseMenuItemContent: React.FunctionComponent<BaseMenuItemProps> = ({
  children,
  icon,
}) => {
  return (
    <>
      {icon && (
        <div className="flex h-5 w-5 items-center justify-center">{icon}</div>
      )}
      {children}
    </>
  );
};

export const MenuItem = {
  ExternalLink: ExternalLinkMenuItem,
  ReactRouterLink: ReactRouterLinkMenuItem,
  Button: ButtonMenuItem,
};

export const MenuItemGroup: React.FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  return <div className="py-1">{children}</div>;
};

type PassthruMenuProps<
  T extends keyof JSX.IntrinsicElements | React.JSXElementConstructor<unknown>,
> = Omit<Parameters<typeof HeadlessMenu<T>>[0], 'ref'>;

type MenuProps<T extends React.ElementType = 'button'> = {
  items: React.ReactNode;
  portal?: boolean;
  rootClassName?: string;
  placement?: React.ComponentProps<typeof Float>['placement'];
  shift?: boolean;
  originClassName?: string;
  disabled?: boolean;
} & PassthruMenuProps<T>;

export function Menu<T extends React.ElementType = 'button'>({
  items,
  portal,
  rootClassName,
  placement = 'bottom-start',
  shift = false,
  originClassName,
  disabled,
  ...rest
}: MenuProps<T>): JSX.Element {
  return (
    <HeadlessMenu as="div" className={classNames('flex', rootClassName)}>
      <Float
        flip
        shift={shift}
        portal={portal}
        offset={8}
        as={Fragment}
        placement={placement}
        tailwindcssOriginClass
        originClass={originClassName}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <HeadlessMenu.Button disabled={disabled} {...rest} />
        <HeadlessMenu.Items className="w-56 divide-y divide-solid divide-purple-100 overflow-hidden rounded-md bg-white shadow-md ring-1 ring-purple-300 focus:outline-none">
          {items}
        </HeadlessMenu.Items>
      </Float>
    </HeadlessMenu>
  );
}
